import { z } from "zod";
import { captureException } from "@sentry/vue";

/**
 * Handles API response validation with proper error handling for quote-api responses.
 *
 * Quote-api returns different structures for success and error cases:
 * - Success: { response: { ...data } }
 * - Error: { errors: [{ message: string }] }
 *
 * This function uses safeParse to handle both cases and reports meaningful errors to Sentry.
 */
export const handleApiResponse = <T = unknown>(
    responseData: unknown,
    successSchema: z.ZodTypeAny,
    functionName: string,
): T => {
    // Try parsing as success response first
    const successResult = successSchema.safeParse(responseData);
    if (successResult.success) {
        return successResult.data;
    }

    // Try parsing as error response
    const errorResult = z.object({
        errors: z.array(z.object({
            message: z.string(),
        })).min(1),
    }).safeParse(responseData);

    if (errorResult.success) {
        // Create meaningful error with API details
        const apiError = new Error(
            `${functionName} API error: ${errorResult.data.errors.map(e => e.message).join(", ")}`,
        );
        captureException(apiError);
        throw apiError;
    }

    // Neither structure matched - capture Zod error with context
    const zodError = new Error(
        `${functionName} validation error: ${successResult.error.message}`,
    );
    captureException(zodError);
    throw zodError;
};
